import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { AutosaveStatus } from '../lib/autosave.js';

type EditorCommand = {
  status: AutosaveStatus;
  save?(): void;
};

type RegistrationContext = {
  set(token: symbol, command: EditorCommand): void;
  clear(token: symbol): void;
};

const fallback: EditorCommand = { status: 'Saved' };
const EditorCommandStateContext = createContext<EditorCommand>(fallback);
const EditorCommandRegistrationContext = createContext<RegistrationContext | null>(null);

export function EditorCommandProvider({ children }: { children: ReactNode }) {
  const [command, setCommand] = useState(fallback);
  const activeToken = useRef<symbol | null>(null);
  const registration = useMemo<RegistrationContext>(
    () => ({
      set(token, next) {
        activeToken.current = token;
        setCommand(next);
      },
      clear(token) {
        if (activeToken.current !== token) return;
        activeToken.current = null;
        setCommand(fallback);
      },
    }),
    [],
  );
  useEffect(() => {
    const saveOnShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 's') return;
      if (!command.save) return;
      event.preventDefault();
      command.save();
    };
    window.addEventListener('keydown', saveOnShortcut);
    return () => window.removeEventListener('keydown', saveOnShortcut);
  }, [command]);
  return (
    <EditorCommandRegistrationContext.Provider value={registration}>
      <EditorCommandStateContext.Provider value={command}>
        {children}
      </EditorCommandStateContext.Provider>
    </EditorCommandRegistrationContext.Provider>
  );
}

export function useEditorCommands() {
  return useContext(EditorCommandStateContext);
}

export function useEditorCommandRegistration(command: EditorCommand) {
  const registration = useContext(EditorCommandRegistrationContext);
  const token = useRef(Symbol('editor-command'));
  const save = useRef(command.save);
  save.current = command.save;

  useEffect(() => {
    if (!registration) return;
    const currentToken = token.current;
    registration.set(currentToken, {
      status: command.status,
      save: () => save.current?.(),
    });
    return () => registration.clear(currentToken);
  }, [command.status, registration]);
}
